"use client";

/**
 * AdminDatePicker — 달력 팝오버 date picker 공통 컴포넌트
 *
 * 입력창을 클릭하면 달력 팝오버가 열리고 날짜를 선택하면 닫힌다.
 * 값은 "YYYY-MM-DD" 문자열로 주고받는다.
 *
 * @example
 * const [date, setDate] = useState("");
 * <AdminDatePicker value={date} onChange={setDate} placeholder="생년월일" />
 *
 * @example // 범위 제한
 * <AdminDatePicker value={date} onChange={setDate} min="2020-01-01" max="2026-12-31" />
 *
 * @example // 지우기 버튼
 * <AdminDatePicker value={date} onChange={setDate} clearable />
 */
import { useState } from "react";
import { CalendarIcon, X } from "lucide-react";

import { cn } from "../../lib/utils";
import { Calendar } from "../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

export interface AdminDatePickerProps {
    /** "YYYY-MM-DD" 날짜 문자열 */
    value?: string;
    onChange?: (value: string) => void;
    /** 빈 값일 때 표시 문구 (기본 "날짜를 선택하세요") */
    placeholder?: string;
    /** 선택 가능 최솟값 ("YYYY-MM-DD") */
    min?: string;
    /** 선택 가능 최댓값 ("YYYY-MM-DD") */
    max?: string;
    /** X 버튼으로 값 지우기 허용 (기본 false) */
    clearable?: boolean;
    disabled?: boolean;
    className?: string;
    /** 입력창 너비 클래스 (기본 w-full) */
    widthClassName?: string;
    id?: string;
    "aria-label"?: string;
}

/** "YYYY-MM-DD" → Date (로컬 타임존) */
function parseDate(ymd: string | undefined): Date | undefined {
    if (!ymd) return undefined;
    const [y, m, d] = ymd.split("-").map(Number);
    if (!y || !m || !d) return undefined;
    return new Date(y, m - 1, d);
}

/** Date → "YYYY-MM-DD" */
function formatDate(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}

export function AdminDatePicker({
    value = "",
    onChange,
    placeholder = "날짜를 선택하세요",
    min,
    max,
    clearable = false,
    disabled,
    className,
    widthClassName = "w-full",
    id,
    "aria-label": ariaLabel,
}: AdminDatePickerProps) {
    const [open, setOpen] = useState(false);

    const selected = parseDate(value);
    const minDate = parseDate(min);
    const maxDate = parseDate(max);

    // 선택값이 없을 때 달력이 열릴 기본 월.
    // max 가 있으면 max 월, 없으면 min 월, 둘 다 없으면 현재 월.
    const defaultMonth = selected ?? maxDate ?? minDate;

    const handleSelect = (date: Date | undefined) => {
        onChange?.(date ? formatDate(date) : "");
        if (date) setOpen(false); // 날짜 선택 시 팝오버 자동 닫기
    };

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        onChange?.("");
    };

    return (
        <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
            <PopoverTrigger asChild>
                <button
                    id={id}
                    type="button"
                    aria-label={ariaLabel ?? placeholder}
                    aria-expanded={open}
                    disabled={disabled}
                    className={cn(
                        "flex h-9 items-center gap-2 rounded-md border border-input bg-transparent px-3 text-sm shadow-xs",
                        "focus-visible:outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
                        "disabled:pointer-events-none disabled:opacity-50",
                        "text-left",
                        widthClassName,
                        className
                    )}
                >
                    <CalendarIcon className="size-4 shrink-0 text-muted-foreground" />
                    <span
                        className={cn(
                            "flex-1 truncate",
                            !value && "text-muted-foreground"
                        )}
                    >
                        {value || placeholder}
                    </span>
                    {/* 지우기 버튼 */}
                    {clearable && value && !disabled && (
                        <span
                            role="button"
                            aria-label="날짜 초기화"
                            onClick={handleClear}
                            className="shrink-0 rounded-sm text-muted-foreground hover:text-foreground"
                        >
                            <X className="size-3.5" />
                        </span>
                    )}
                </button>
            </PopoverTrigger>

            <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                    mode="single"
                    selected={selected}
                    onSelect={handleSelect}
                    defaultMonth={defaultMonth}
                    // 드롭다운 연도·월 범위를 min/max 에 맞게 제한
                    {...(minDate ? { startMonth: minDate } : {})}
                    {...(maxDate ? { endMonth: maxDate } : {})}
                    // min/max 날짜 바깥은 선택 비활성 (react-day-picker v10 matcher)
                    {...(minDate || maxDate
                        ? {
                              disabled: [
                                  ...(minDate ? [{ before: minDate }] : []),
                                  ...(maxDate ? [{ after: maxDate }] : []),
                              ],
                          }
                        : {})}
                />
            </PopoverContent>
        </Popover>
    );
}
